/**
 * Tema do checkout lembrado por caminho, para a PRIMEIRA PINTURA.
 *
 * O tema só é conhecido depois de três respostas em cadeia (conteúdo →
 * checkout → página). Até lá a tela pintava no claro, e num checkout escuro o
 * comprador via um clarão branco antes do conteúdo. Não há como saber o tema
 * antes da API numa SPA — mas dá para lembrar o da última visita a este mesmo
 * link e aplicá-lo antes de o React montar.
 *
 * É só um palpite para o carregamento: quando as respostas chegam, o tema real
 * é aplicado por cima (ver `useCheckoutPage`), então um valor velho se corrige
 * sozinho. Na primeira visita não há o que lembrar e vale o comportamento antigo.
 */
const PREFIX = 'checkout-theme:';
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

type CachedTheme = { dark: boolean; at: number };

const isPreview = () =>
	new URLSearchParams(window.location.search).get('_preview') === '1';

const keyFor = (pathname: string) => `${PREFIX}${pathname}`;

export const readCachedTheme = (
	pathname: string = window.location.pathname
): boolean | null => {
	try {
		const raw = localStorage.getItem(keyFor(pathname));
		if (!raw) return null;

		const cached = JSON.parse(raw) as CachedTheme;
		if (
			typeof cached?.dark !== 'boolean' ||
			typeof cached?.at !== 'number' ||
			Date.now() - cached.at > TTL_MS
		) {
			return null;
		}
		return cached.dark;
	} catch {
		// storage bloqueado ou valor corrompido: sem palpite.
		return null;
	}
};

export const writeCachedTheme = (
	dark: boolean,
	pathname: string = window.location.pathname
): void => {
	// O preview do builder troca de tema a cada clique: não é visita real.
	if (isPreview()) return;
	try {
		const value: CachedTheme = { dark, at: Date.now() };
		localStorage.setItem(keyFor(pathname), JSON.stringify(value));
	} catch {
		// storage indisponível: só perde o palpite da próxima visita.
	}
};

/** Aplica o tema lembrado antes de o React montar. */
export const bootCachedTheme = (): void => {
	if (typeof document === 'undefined' || isPreview()) return;
	if (readCachedTheme() === true) document.body.classList.add('dark-mode');
};
